/**
 * Weekly menu route (Organizers only)
 * Composition assistant + weekly nutrition synthesis
 */

import { createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { requireOrganizer } from '@/lib/auth/routeGuards';
import {
  useAssistant,
  useWeekMenu,
  useWeekSummary,
  useSetMenuEntry,
  useRemoveMenuEntry,
} from '@/hooks/useMenus';
import { Loading } from '@/components/ui/Loading';
import { NutritionSummary } from '@/components/dishes/NutritionSummary';
import { useToast } from '@/components/ui/ToastProvider';
import type {
  AssistantCandidate,
  AssistantOptions,
  MenuEntryView,
} from '@/types/api';

export const Route = createFileRoute('/menu/')({
  beforeLoad: requireOrganizer,
  component: MenuComponent,
});

interface Slot {
  date: string;
  mealType: 'lunch' | 'dinner';
}

const DAY_LABELS = ['lun', 'mar', 'mer', 'jeu', 'ven', 'sam', 'dim'];

function toISO(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function currentMonday(): string {
  const d = new Date();
  const day = (d.getDay() + 6) % 7; // 0 = Monday
  d.setDate(d.getDate() - day);
  return toISO(d);
}

function addDays(iso: string, days: number): string {
  const [y, m, d] = iso.split('-').map(Number);
  const dt = new Date(y, m - 1, d);
  dt.setDate(dt.getDate() + days);
  return toISO(dt);
}

function MenuComponent() {
  const { t } = useTranslation();
  const [weekStart, setWeekStart] = useState(currentMonday());
  const [selectedSlot, setSelectedSlot] = useState<Slot | null>(null);

  const { data: week, isLoading } = useWeekMenu(weekStart);
  const { data: summary } = useWeekSummary(weekStart);

  const entryBySlot = new Map<string, MenuEntryView>();
  for (const entry of week?.entries ?? []) {
    entryBySlot.set(`${entry.date}-${entry.mealType}`, entry);
  }

  const days = Array.from({ length: 7 }, (_, i) => addDays(weekStart, i));

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              {t('menu.title')}
            </h1>
            <p className="mt-1 text-sm text-gray-600">{t('menu.description')}</p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setWeekStart(addDays(weekStart, -7))}
              className="px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              ←
            </button>
            <button
              onClick={() => setWeekStart(currentMonday())}
              className="px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              {t('menu.thisWeek')}
            </button>
            <button
              onClick={() => setWeekStart(addDays(weekStart, 7))}
              className="px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              →
            </button>
          </div>
        </div>

        {isLoading && <Loading />}

        <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-3 mb-8">
          {days.map((date, index) => (
            <div key={date} className="space-y-2">
              <div className="text-center text-sm font-medium text-gray-700">
                {DAY_LABELS[index]} {date.slice(8)}
              </div>
              {(['lunch', 'dinner'] as const).map((mealType) => {
                const entry = entryBySlot.get(`${date}-${mealType}`);
                const isSelected =
                  selectedSlot?.date === date &&
                  selectedSlot?.mealType === mealType;
                return (
                  <button
                    key={mealType}
                    onClick={() => setSelectedSlot({ date, mealType })}
                    className={`w-full text-left rounded-lg border p-2 min-h-[5rem] transition-colors ${
                      isSelected
                        ? 'border-indigo-500 ring-2 ring-indigo-200 bg-white'
                        : 'border-gray-200 bg-white hover:border-indigo-300'
                    }`}
                  >
                    <div className="text-xs uppercase text-gray-400 mb-1">
                      {t(`availability.${mealType}`)}
                    </div>
                    {entry ? (
                      <>
                        <div className="text-sm font-medium text-gray-900">
                          {entry.dishName}
                        </div>
                        <div className="text-xs text-gray-500">
                          {Math.round(entry.perServing.energyKcal)} kcal
                        </div>
                      </>
                    ) : (
                      <div className="text-xs text-gray-400">
                        {t('menu.emptySlot')}
                      </div>
                    )}
                  </button>
                );
              })}
            </div>
          ))}
        </div>

        {selectedSlot && (
          <AssistantPanel
            slot={selectedSlot}
            currentEntry={entryBySlot.get(
              `${selectedSlot.date}-${selectedSlot.mealType}`,
            )}
            onClose={() => setSelectedSlot(null)}
          />
        )}

        {/* Weekly synthesis */}
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <h2 className="text-base font-semibold text-gray-900 mb-1">
            {t('menu.summaryTitle')}
          </h2>
          <p className="text-sm text-gray-500 mb-4">
            {t('menu.summaryDescription')}
          </p>
          {(summary?.members ?? []).length === 0 ? (
            <p className="text-sm text-gray-500">{t('menu.noSummary')}</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead>
                  <tr className="text-left text-xs font-medium text-gray-500 uppercase">
                    <th className="py-2 pr-4">{t('menu.member')}</th>
                    <th className="py-2 pr-4">{t('menu.goal')}</th>
                    <th className="py-2 pr-4">{t('menu.total')}</th>
                    <th className="py-2 pr-4">{t('menu.averagePerDay')}</th>
                    <th className="py-2 pr-4">{t('menu.averagePerMeal')}</th>
                    <th className="py-2 pr-4">{t('menu.meals')}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 text-sm">
                  {summary!.members.map((member) => (
                    <tr key={member.userId}>
                      <td className="py-2 pr-4 font-medium text-gray-900">
                        {member.userName}
                      </td>
                      <td className="py-2 pr-4 text-gray-600">
                        {t(`goal.${member.goal}`)}
                      </td>
                      <td className="py-2 pr-4 text-gray-900">
                        {Math.round(member.total.energyKcal)} kcal
                      </td>
                      <td className="py-2 pr-4 text-gray-600">
                        {Math.round(member.averagePerDay.energyKcal)} kcal
                      </td>
                      <td className="py-2 pr-4 text-gray-600">
                        {Math.round(member.averagePerMeal.energyKcal)} kcal
                      </td>
                      <td className="py-2 pr-4 text-gray-600">
                        {member.mealCount}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

interface AssistantPanelProps {
  slot: Slot;
  currentEntry?: MenuEntryView;
  onClose: () => void;
}

function AssistantPanel({ slot, currentEntry, onClose }: AssistantPanelProps) {
  const { t } = useTranslation();
  const toast = useToast();
  const [options, setOptions] = useState<AssistantOptions>({
    includeOutOfGoal: false,
    ignoreDishPreferences: false,
    ignoreIngredientPreferences: false,
  });

  const { data, isFetching } = useAssistant({ ...slot, ...options });
  const setEntry = useSetMenuEntry();
  const removeEntry = useRemoveMenuEntry();

  const handlePick = async (candidate: AssistantCandidate) => {
    try {
      await setEntry.mutateAsync({
        date: slot.date,
        mealType: slot.mealType,
        dishId: candidate.id,
        removedIngredientIds: candidate.removedIngredients.map(
          (r) => r.ingredientId,
        ),
      });
      toast.success(t('menu.saved'));
      onClose();
    } catch {
      toast.error(t('menu.errorSaving'));
    }
  };

  const handleRemove = async () => {
    try {
      await removeEntry.mutateAsync({
        date: slot.date,
        mealType: slot.mealType,
      });
      toast.success(t('menu.removed'));
      onClose();
    } catch {
      toast.error(t('menu.errorSaving'));
    }
  };

  return (
    <div className="bg-white rounded-xl border border-indigo-200 p-6 mb-8">
      <div className="flex items-start justify-between mb-4">
        <div>
          <h2 className="text-base font-semibold text-gray-900">
            {t('menu.assistantTitle')}
          </h2>
          <p className="text-sm text-gray-500">
            {slot.date} · {t(`availability.${slot.mealType}`)}
          </p>
        </div>
        <button
          onClick={onClose}
          className="text-gray-400 hover:text-gray-600"
          aria-label={t('common.close')}
        >
          ✕
        </button>
      </div>

      {/* Present members and binding goal */}
      <div className="mb-4 flex flex-wrap items-center gap-2 text-sm">
        <span className="font-medium text-gray-700">
          {t('menu.presentMembers')}:
        </span>
        {(data?.presentMembers ?? []).length === 0 ? (
          <span className="text-gray-400">{t('menu.noPresent')}</span>
        ) : (
          data!.presentMembers.map((member) => (
            <span
              key={member.id}
              className="inline-flex items-center px-2 py-0.5 rounded bg-gray-100 text-gray-700"
            >
              {member.name} · {t(`goal.${member.goal}`)}
            </span>
          ))
        )}
        {data?.bindingGoal && (
          <span className="inline-flex items-center px-2 py-0.5 rounded bg-indigo-100 text-indigo-800 font-medium">
            {t('menu.bindingGoal')}: {t(`goal.${data.bindingGoal}`)}
          </span>
        )}
      </div>

      {/* Options */}
      <div className="mb-4 flex flex-wrap gap-4 text-sm">
        <label className="inline-flex items-center gap-2">
          <input
            type="checkbox"
            checked={options.includeOutOfGoal}
            onChange={(e) =>
              setOptions((o) => ({ ...o, includeOutOfGoal: e.target.checked }))
            }
            className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
          />
          {t('menu.includeOutOfGoal')}
        </label>
        <label className="inline-flex items-center gap-2">
          <input
            type="checkbox"
            checked={options.ignoreDishPreferences}
            onChange={(e) =>
              setOptions((o) => ({
                ...o,
                ignoreDishPreferences: e.target.checked,
              }))
            }
            className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
          />
          {t('menu.ignoreDishPreferences')}
        </label>
        <label className="inline-flex items-center gap-2">
          <input
            type="checkbox"
            checked={options.ignoreIngredientPreferences}
            onChange={(e) =>
              setOptions((o) => ({
                ...o,
                ignoreIngredientPreferences: e.target.checked,
              }))
            }
            className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
          />
          {t('menu.ignoreIngredientPreferences')}
        </label>
      </div>

      {isFetching && <Loading />}

      {(data?.candidates ?? []).length === 0 && !isFetching && (
        <p className="text-sm text-gray-500 mb-4">{t('menu.noCandidates')}</p>
      )}

      <ul className="space-y-2">
        {(data?.candidates ?? []).map((candidate) => (
          <li
            key={candidate.id}
            className="flex flex-wrap items-center justify-between gap-3 border border-gray-200 rounded-lg p-3"
          >
            <div className="min-w-[14rem]">
              <div className="flex items-center gap-2">
                <span className="font-medium text-gray-900">
                  {candidate.name}
                </span>
                <span
                  className={`text-xs px-1.5 py-0.5 rounded ${
                    candidate.compliantWithBindingGoal
                      ? 'bg-green-100 text-green-800'
                      : 'bg-orange-100 text-orange-800'
                  }`}
                >
                  {candidate.compliantWithBindingGoal
                    ? t('menu.compliant')
                    : t('menu.outOfGoal')}
                </span>
              </div>
              <div className="flex flex-wrap gap-1 mt-1">
                {candidate.goals.map((goal) => (
                  <span
                    key={goal}
                    className="text-xs px-1.5 py-0.5 rounded bg-gray-100 text-gray-600"
                  >
                    {t(`goal.${goal}`)}
                  </span>
                ))}
              </div>
              {candidate.removedIngredients.length > 0 && (
                <p className="text-xs text-blue-700 mt-1">
                  {t('menu.without')}{' '}
                  {candidate.removedIngredients
                    .map((r) => r.productName)
                    .join(', ')}
                </p>
              )}
              {candidate.avoidedIngredients.length > 0 && (
                <p className="text-xs text-orange-600 mt-1">
                  {t('menu.avoids')}{' '}
                  {candidate.avoidedIngredients
                    .map((r) => r.productName)
                    .join(', ')}
                </p>
              )}
              {candidate.likedBy.length > 0 && (
                <p className="text-xs text-green-700 mt-1">
                  ♥ {candidate.likedBy.map((m) => m.name).join(', ')}
                </p>
              )}
              {candidate.dislikedBy.length > 0 && (
                <p className="text-xs text-red-600 mt-1">
                  ✗ {candidate.dislikedBy.map((m) => m.name).join(', ')}
                </p>
              )}
            </div>

            <NutritionSummary nutrition={candidate.perServing} />

            <button
              onClick={() => handlePick(candidate)}
              disabled={setEntry.isPending}
              className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 disabled:opacity-50"
            >
              {t('menu.pick')}
            </button>
          </li>
        ))}
      </ul>

      {currentEntry && (
        <button
          onClick={handleRemove}
          disabled={removeEntry.isPending}
          className="mt-4 text-sm font-medium text-red-600 hover:text-red-800"
        >
          {t('menu.removeEntry')}
        </button>
      )}
    </div>
  );
}
